import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSpotifyPlayer } from "./useSpotifyPlayer";
import { Play, Pause, SkipBack, SkipForward, Search, Volume2, Music2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SpotifyPanel({ status, isDispatcher, onConnect, onDisconnect }) {
  const connected = !!status?.connected;
  const sp = useSpotifyPlayer(connected);

  if (!status) return <div className="p-10 text-[#A1A1AA]">Loading music…</div>;
  if (!status.configured) return <Notice title="Music unavailable" body="Spotify isn't configured on this server yet."/>;
  if (!connected) {
    return (
      <Notice title="Connect Spotify" body={isDispatcher
        ? "Sign in once with the company Spotify Premium account. Every tablet will then play music without logging in."
        : "Music isn't connected yet — please ask dispatch to connect the company Spotify account."}>
        {isDispatcher && <button data-testid="spotify-connect-btn" onClick={onConnect} className="btn-gold mt-6 !px-8 !py-4 text-xs">Connect Spotify</button>}
      </Notice>
    );
  }

  return (
    <section data-testid="spotify-panel" className="h-full flex flex-col p-6 md:p-8 gap-6 overflow-hidden">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <SpotifyMark/>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A1A1AA]">Music</div>
            <div className="text-sm text-[#F5F2EB]" data-testid="spotify-account">
              {status.account || "Spotify"} · <span className={sp.ready ? "text-emerald-400" : "text-[#71717A]"} data-testid="spotify-device-state">{sp.ready ? "Tablet speaker ready" : "Starting player…"}</span>
            </div>
          </div>
        </div>
        {isDispatcher && <button data-testid="spotify-disconnect-btn" onClick={onDisconnect} className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] hover:text-red-400">Disconnect</button>}
      </div>

      {sp.error && <div data-testid="spotify-error" className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3">{sp.error}</div>}

      <NowPlaying sp={sp}/>

      <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-8">
        <TrackSearch onPlay={(uri) => sp.play({ uris: [uri] })}/>
        <MoodGrid onPlay={async (key) => { const ok = await sp.play({ mood: key }); if (ok) toast.success("Playing", { duration: 1200 }); }}/>
      </div>
    </section>
  );
}

function Notice({ title, body, children }) {
  return (
    <div data-testid="spotify-notice" className="h-full flex flex-col items-center justify-center text-center p-10">
      <SpotifyMark size={48}/>
      <h2 className="font-serif text-3xl text-[#F5F2EB] mt-6 mb-3">{title}</h2>
      <p className="text-[#A1A1AA] max-w-sm">{body}</p>
      {children}
    </div>
  );
}

function SpotifyMark({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="shrink-0">
      <circle cx="12" cy="12" r="12" fill="#1DB954"/>
      <path d="M6.5 9.3c3.4-1 7.6-.8 10.9 1.1M7.2 12.3c2.8-.8 6.2-.6 8.9.9M7.9 15.1c2.2-.6 4.8-.4 6.9.7" stroke="#0a0a0a" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
    </svg>
  );
}

function NowPlaying({ sp }) {
  const t = sp.state?.track_window?.current_track;
  const paused = sp.state?.paused ?? true;
  const [pos, setPos] = useState(0);
  useEffect(() => {
    if (!sp.state) return;
    setPos(sp.state.position);
    if (sp.state.paused) return;
    const started = Date.now();
    const id = setInterval(() => setPos(Math.min(sp.state.duration, sp.state.position + (Date.now() - started))), 1000);
    return () => clearInterval(id);
  }, [sp.state]);
  const dur = sp.state?.duration || 0;
  const btn = "w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center border border-white/15 text-[#F5F2EB] active:scale-95 transition-transform disabled:opacity-30";

  return (
    <div data-testid="now-playing" className="border border-white/10 bg-[#0A0A0A] p-4 md:p-5">
      <div className="flex items-center gap-4">
        {t?.album?.images?.[0]?.url
          ? <img src={t.album.images[0].url} alt="" className="w-20 h-20 md:w-24 md:h-24 object-cover" data-testid="now-playing-art"/>
          : <div className="w-20 h-20 md:w-24 md:h-24 bg-white/5 flex items-center justify-center"><Music2 className="text-[#71717A]"/></div>}
        <div className="flex-1 min-w-0">
          <div className="font-serif text-xl md:text-2xl text-[#F5F2EB] truncate" data-testid="now-playing-title">{t?.name || "Nothing playing"}</div>
          <div className="text-sm text-[#A1A1AA] truncate" data-testid="now-playing-artist">{t ? t.artists.map(a => a.name).join(", ") : "Pick a mood or search for a song"}</div>
          <div className="mt-3 h-1 bg-white/10 relative">
            <div className="absolute inset-y-0 left-0 bg-[#D4AF37]" style={{ width: dur ? `${(pos / dur) * 100}%` : "0%" }}/>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <button data-testid="player-prev" className={btn} onClick={sp.previous} disabled={!sp.ready} aria-label="Previous"><SkipBack size={22}/></button>
          <button data-testid="player-toggle" className={`${btn} !bg-[#D4AF37] !text-[#050505] !border-[#D4AF37] w-16 h-16 md:w-20 md:h-20`} onClick={sp.togglePlay} disabled={!sp.ready} aria-label={paused ? "Play" : "Pause"}>
            {paused ? <Play size={26} className="ml-1"/> : <Pause size={26}/>}
          </button>
          <button data-testid="player-next" className={btn} onClick={sp.next} disabled={!sp.ready} aria-label="Next"><SkipForward size={22}/></button>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3 text-[#A1A1AA]">
        <Volume2 size={16}/>
        <input data-testid="player-volume" type="range" min="0" max="1" step="0.02" value={sp.volume}
          onChange={e => sp.setVolume(parseFloat(e.target.value))} className="flex-1 accent-[#D4AF37] h-6"/>
      </div>
    </div>
  );
}

function TrackSearch({ onPlay }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); return; }
    const id = setTimeout(async () => {
      setBusy(true);
      try { const r = await api.get("/spotify/search", { params: { q: term } }); setResults(r.data.tracks); }
      catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); }
      finally { setBusy(false); }
    }, 350);
    return () => clearTimeout(id);
  }, [q]);

  return (
    <div data-testid="track-search">
      <div className="relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A]"/>
        <input data-testid="track-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search any song or artist"
          className="w-full bg-[#0A0A0A] border border-white/15 pl-12 pr-12 py-4 text-base text-[#F5F2EB] placeholder-[#52525B] focus:outline-none focus:border-[#D4AF37]"/>
        {busy ? <Loader2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-[#71717A]"/>
          : q && <button data-testid="track-search-clear" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-[#71717A]" aria-label="Clear"><X size={18}/></button>}
      </div>
      {results && (
        <div className="mt-3 divide-y divide-white/5 border border-white/10 bg-[#0A0A0A]" data-testid="track-results">
          {results.length === 0 && <div className="p-4 text-sm text-[#A1A1AA]">No songs found.</div>}
          {results.map(t => (
            <button key={t.id} data-testid={`track-${t.id}`} onClick={() => onPlay(t.uri)}
              className="w-full flex items-center gap-4 p-3 text-left hover:bg-white/5 active:bg-white/10 transition-colors">
              {t.image ? <img src={t.image} alt="" className="w-12 h-12 object-cover"/> : <div className="w-12 h-12 bg-white/5"/>}
              <div className="flex-1 min-w-0">
                <div className="text-[#F5F2EB] truncate">{t.name}</div>
                <div className="text-xs text-[#A1A1AA] truncate">{t.artists} · {t.album}</div>
              </div>
              <Play size={18} className="text-[#D4AF37] shrink-0"/>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MoodGrid({ onPlay }) {
  const [moods, setMoods] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/spotify/moods").then(r => setMoods(r.data.moods)).catch(() => {}); }, []);
  return (
    <div data-testid="mood-grid">
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#A1A1AA] mb-3">Mood playlists</div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {moods.map(m => (
          <button key={m.key} data-testid={`mood-${m.key}`} onClick={() => { setActive(m.key); onPlay(m.key); }}
            className={`relative h-24 md:h-28 p-4 text-left border transition-transform active:scale-[0.98] overflow-hidden ${active === m.key ? "border-[#D4AF37]" : "border-white/10"}`}
            style={{ background: `linear-gradient(135deg, ${m.color}66, #0A0A0A 70%)` }}>
            <div className="font-serif text-2xl text-[#F5F2EB]">{m.label}</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#A1A1AA] mt-1">{m.sub}</div>
            <Play size={18} className="absolute right-4 bottom-4 text-[#D4AF37]"/>
          </button>
        ))}
      </div>
    </div>
  );
}
